import { FC, useEffect } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Box,
	TextField,
	FormControlLabel,
	Switch,
	CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createDeliveryOperatorRequest,
	updateDeliveryOperatorRequest,
	resetCreateDeliveryOperatorSuccess,
	resetUpdateDeliveryOperatorSuccess,
} from 'redux/reducers/euDdp/reducer';
import {
	selectCreateDeliveryOperatorLoading,
	selectCreateDeliveryOperatorSuccess,
	selectUpdateDeliveryOperatorLoading,
	selectUpdateDeliveryOperatorSuccess,
} from 'redux/reducers/euDdp/selectors';
import { TDeliveryOperatorDialogProps } from './types';

const validationSchema = Yup.object({
	name: Yup.string().required('Name is required').max(100, 'Max 100 characters'),
	code: Yup.string().required('Code is required').max(50, 'Max 50 characters'),
	isActive: Yup.boolean().required(),
});

export const DeliveryOperatorDialog: FC<TDeliveryOperatorDialogProps> = ({
	open,
	onClose,
	operatorToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!operatorToEdit;

	const createLoading = useSelector(selectCreateDeliveryOperatorLoading);
	const createSuccess = useSelector(selectCreateDeliveryOperatorSuccess);
	const updateLoading = useSelector(selectUpdateDeliveryOperatorLoading);
	const updateSuccess = useSelector(selectUpdateDeliveryOperatorSuccess);

	const loading = createLoading || updateLoading;

	const formik = useFormik({
		initialValues: {
			name: operatorToEdit?.name ?? '',
			code: operatorToEdit?.code ?? '',
			isActive: operatorToEdit?.isActive ?? true,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				name: values.name,
				code: values.code,
				isActive: values.isActive,
			};
			if (isEdit && operatorToEdit) {
				dispatch(updateDeliveryOperatorRequest({ id: operatorToEdit.id, body }));
			} else {
				dispatch(createDeliveryOperatorRequest(body));
			}
		},
	});

	useEffect(() => {
		if (createSuccess || updateSuccess) {
			dispatch(resetCreateDeliveryOperatorSuccess());
			dispatch(resetUpdateDeliveryOperatorSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createSuccess, updateSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isEdit ? 'Edit Delivery Operator' : 'Add New Delivery Operator'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="name"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && !!formik.errors.name}
						helperText={formik.touched.name && formik.errors.name}
						size="small"
					/>
					<TextField
						label="Code"
						name="code"
						value={formik.values.code}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.code && !!formik.errors.code}
						helperText={formik.touched.code && formik.errors.code}
						size="small"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={formik.values.isActive}
								onChange={(e) => {
									void formik.setFieldValue('isActive', e.target.checked);
								}}
								name="isActive"
							/>
						}
						label="Active"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
